import { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'w-full rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-wine focus:ring-2 focus:ring-wine/20 transition-colors';

const PublicForm = ({ formId = null }) => {
  const [form, setForm] = useState(null);
  const [live, setLive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (formId) {
      axios.get(`${BACKEND_URL}/api/retreat/forms/${formId}/public`)
        .then(res => { setLive(res.data.open); setForm(res.data.form); })
        .catch(() => setLive(false));
      return;
    }
    axios.get(`${BACKEND_URL}/api/retreat/forms/live`)
      .then(res => {
        setLive(res.data.live);
        if (res.data.live) setForm(res.data.form);
      })
      .catch(() => setLive(false));
  }, [formId]);

  const isSurvey = !!formId;

  const setAnswer = (fieldId, value) => setAnswers(a => ({ ...a, [fieldId]: value }));

  const toggleCheckbox = (fieldId, option) => {
    const current = answers[fieldId] || [];
    setAnswer(fieldId, current.includes(option) ? current.filter(o => o !== option) : [...current, option]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const missing = form.fields.filter(f => {
      const v = answers[f.id];
      return f.required && (v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
    });
    if (missing.length > 0) {
      setError(`Please answer: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/forms/${form.id}/submit`, { answers });
      setSuccessMessage(res.data.success_message || 'Thank you! Your response has been recorded.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (live === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-wine" />
      </div>
    );
  }

  if (live === false) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-2 border-gray-200 text-center" data-testid="form-offline-notice">
        <p className="text-lg font-semibold text-gray-900 mb-2">{isSurvey ? 'This survey is closed.' : 'The waitlist form is temporarily unavailable.'}</p>
        <p className="text-gray-600">
          Please email{' '}
          <a href="mailto:info@rojoretreat.com" className="text-wine-700 underline font-semibold">info@rojoretreat.com</a>{' '}
          and we&apos;ll add you to the waitlist directly.
        </p>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-2 border-emerald-200 text-center" data-testid="form-success-message">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
        <p className="text-xl font-bold text-gray-900 mb-3">{isSurvey ? 'Thank you.' : "You're on the list."}</p>
        <p className="text-gray-700 leading-relaxed max-w-xl mx-auto">{successMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden" data-testid="public-form">
      <div className="bg-gradient-to-r from-wine to-burgundy px-6 md:px-10 py-6 md:py-8">
        <h3 className="text-xl md:text-2xl font-bold text-white">{form.title}</h3>
        {form.description && (
          <p className="text-sm md:text-base text-white/80 mt-2 leading-relaxed">{form.description}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-6 md:p-10 space-y-7" noValidate>
        {form.fields.map((field) => (
          <div key={field.id} data-testid={`public-field-${field.id}`}>
            <label className="block text-sm md:text-base font-semibold text-gray-900 mb-1.5">
              {field.label}
              {field.required && <span className="text-wine-700 ml-1">*</span>}
            </label>
            {field.help_text && <p className="text-xs md:text-sm text-gray-500 mb-2">{field.help_text}</p>}

            {(field.type === 'short_text') && (
              <input type="text" className={inputClass} placeholder={field.placeholder}
                value={answers[field.id] || ''} onChange={(e) => setAnswer(field.id, e.target.value)} />
            )}
            {field.type === 'email' && (
              <input type="email" className={inputClass} placeholder={field.placeholder}
                value={answers[field.id] || ''} onChange={(e) => setAnswer(field.id, e.target.value)} />
            )}
            {field.type === 'phone' && (
              <input type="tel" className={inputClass} placeholder={field.placeholder}
                value={answers[field.id] || ''} onChange={(e) => setAnswer(field.id, e.target.value)} />
            )}
            {field.type === 'number' && (
              <input type="number" className={inputClass} placeholder={field.placeholder}
                value={answers[field.id] || ''} onChange={(e) => setAnswer(field.id, e.target.value)} />
            )}
            {field.type === 'long_text' && (
              <textarea rows={4} className={inputClass} placeholder={field.placeholder}
                value={answers[field.id] || ''} onChange={(e) => setAnswer(field.id, e.target.value)} />
            )}
            {field.type === 'dropdown' && (
              <select className={inputClass} value={answers[field.id] || ''}
                onChange={(e) => setAnswer(field.id, e.target.value)}>
                <option value="">Select…</option>
                {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            )}
            {(field.type === 'radio' || field.type === 'yes_no') && (
              <div className="space-y-2.5 mt-1">
                {(field.type === 'yes_no' ? ['Yes', 'No'] : field.options || []).map(opt => (
                  <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                    <input type="radio" name={field.id} value={opt} checked={answers[field.id] === opt}
                      onChange={() => setAnswer(field.id, opt)}
                      className="h-4 w-4 accent-[#7c1d33]" />
                    <span className="text-gray-800 group-hover:text-gray-950">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {field.type === 'checkboxes' && (
              <div className="space-y-2.5 mt-1">
                {(field.options || []).map(opt => (
                  <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                    <input type="checkbox" checked={(answers[field.id] || []).includes(opt)}
                      onChange={() => toggleCheckbox(field.id, opt)}
                      className="h-4 w-4 rounded accent-[#7c1d33]" />
                    <span className="text-gray-800 group-hover:text-gray-950">{opt}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg px-4 py-3 text-sm text-red-800" data-testid="form-error-message">
            {error}
          </div>
        )}

        <button type="submit" disabled={submitting} data-testid="public-form-submit-btn"
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-wine to-burgundy text-white font-bold text-base md:text-lg py-4 rounded-xl hover:opacity-90 disabled:opacity-60 transition-opacity shadow-lg">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          {submitting ? 'Submitting…' : isSurvey ? 'Submit my feedback' : 'Join the Waitlist'}
        </button>
      </form>
    </div>
  );
};

export default PublicForm;
